import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "components/Layout";
import { usePageTracking } from "hooks/usePageTracking";
import SignIn from "pages/auth/SignIn";
import ForgotPassword from "pages/auth/ForgotPassword";
import ResetPasswordConfirmation from "pages/auth/ResetPasswordConfirmation";
import UserResetPassword from "pages/auth/UserResetPassword";
import TermsConditions from "pages/auth/TermsConditions";
import TemporaryAccessDecision from "pages/temporaryAccess/TemporaryAccessDecision";
import ProtectedRoute from "components/ProtectedRoute";
import AdminRoute from "components/AdminRoute";
import { HomeRedirect } from "components/HomeRedirect";
import DevicesRoutes from "./DevicesRoutes";
import DealersRoutes from "./DealersRoutes";
import UsersRoutes from "./UsersRoutes";
import Profile from "pages/profile/Profile";
import EcoSaltUpgradeRoute from "components/EcoSaltUpgradeRoute";
import EcoSaltUpgrade from "pages/ecosaltUpgrade/EcoSaltUpgrade";

const PageTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  usePageTracking();
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <PageTracker>
        <Routes>
          <Route path="/auth/signin" element={<SignIn />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/auth/reset-password-confirmation"
            element={<ResetPasswordConfirmation />}
          />
          <Route
            path="/auth/reset-user-password"
            element={<UserResetPassword />}
          />
          <Route path="/terms-conditions" element={<TermsConditions />} />

          {/* Customer email links must work without a portal sign-in. */}
          <Route
            path="/temporary-access/decision/*"
            element={<TemporaryAccessDecision mode="decision" />}
          />
          <Route
            path="/temporary-access/cancel/*"
            element={<TemporaryAccessDecision mode="cancel" />}
          />
          {/* Compatibility path for old cancellation links. */}
          <Route
            path="/temporary-access/revoke"
            element={<TemporaryAccessDecision mode="cancel" />}
          />

          <Route path="/" element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route index element={<HomeRedirect />} />
              <Route path="devices/*" element={<DevicesRoutes />} />
              <Route path="profile" element={<Profile />} />
              <Route element={<EcoSaltUpgradeRoute />}>
                <Route path="ecosalt-upgrade" element={<EcoSaltUpgrade />} />
              </Route>

              <Route element={<AdminRoute />}>
                <Route path="dealers/*" element={<DealersRoutes />} />
                <Route path="users/*" element={<UsersRoutes />} />
                <Route
                  path="all-devices/*"
                  element={<DevicesRoutes isAdminView={true} />}
                />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/auth/signin" replace />} />
        </Routes>
      </PageTracker>
    </BrowserRouter>
  );
};

export default AppRoutes;
